import { DownOutlined } from "@ant-design/icons";
import { Dropdown } from "antd";
import usePopUpAtOnce from "components/hooks/usePopUpAtOnce";
import { useAlert } from "components/UI/alertSakeBar/alertProvider";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useCurrentClass } from "store/classes/selectors";
import { useSentenceView } from "store/main_book_popup/selectors";
import { setPopUpHandler } from "store/sentenceStarter/actions";
import { useSentenceStarter, useSentenceStarterPopUp } from "store/sentenceStarter/selectors";
import { useCurrentUser } from "store/users/selectors";
import styled from "styled-components";
import COLOR from "utils/constants/color";
import { useShareNotification } from "utils/methods/firebaseShareAssignment";

const SentenceViewTooling = ({ pageId, book }) => {
  const showAlert = useAlert();
  const currentUser = useCurrentUser();
  const bookData = useMemo(() => {
    return {
      superLesson: book?.superLesson || {},
      differnation: book?.differentiationLesson || {},
      sentenceData: book?.sentence_starter_data || {},
    };
  }, [book, pageId]);
  const { invokeSentenceView, revokeSentenceView, sentenceView } = useSentenceView();
  const dispatch = useDispatch();
  const [sentenceData, setSentenceData] = useState({});
  const { listenToSentenceData } = useShareNotification();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const sentenceStarterRes = useSentenceStarter();
  const currentClass = useCurrentClass();
  const {updatePopUpAtOnce,checkPopUpAtOnce} = usePopUpAtOnce();
  const sentenceStarterPopUp = useSentenceStarterPopUp();
  const currentPageIndex = useMemo(() => {
    return book?.pages?.findIndex(p => p.pageId == pageId);
  }, [book?.pages, pageId]);

  const studentDataFilter = useMemo(() => {
    if (sentenceStarterRes && sentenceStarterRes?.length > 0) {
      return (
        sentenceStarterRes?.filter(
          (x) => x.pageId == pageId && x.class_id === currentClass?.id
        ) || []
      );
    } else {
      return [];
    }
  }, [pageId, sentenceStarterRes]);

  const handleVisibleChange = (visible) => {
    setIsDropdownOpen(visible);
  };

  const hasData = useMemo(() => {
    try{
      const pages = book?.pages || [];
      const isPageInRange = (item) => {
        if (!item.from || !item.to || !pages.length) {
          // Fallback to page number comparison if from/to not available
          return Number(pageId) === Number(item.page);
        }
        
        // Convert from and to pageIds to indices
        const fromIndex = pages.findIndex(x => x.pageId == item.from);
        const toIndex = pages.findIndex(x => x.pageId == item.to);
        
        //console.log(fromIndex, toIndex, currentPageIndex,123)
        // Check if currentPageIndex is within the range (inclusive)
        return currentPageIndex >= fromIndex && currentPageIndex <= toIndex && fromIndex >= 0 && toIndex >= 0;
      };

    return {
      superLesson: bookData?.superLesson
        ? (() => {
            const foundItem = bookData?.superLesson?.find((p) =>
              p.page == pageId
            );
            return foundItem || null;
          })()
        : null,

      differnationLesson: bookData?.differnation
        ? (() => {
            const foundItem = bookData?.differnation?.find((p) =>
              p.page == pageId
            );
            return foundItem || null;
          })()
        : null,

      sentenceData: bookData?.sentenceData
        ? (() => {
            const foundItem = bookData?.sentenceData?.find((p) =>
              p.page == pageId
            );
            return foundItem || null;
          })()
        : null,
    };
    }catch(error){
      console.error(error);
    }
  }, [bookData, pageId]);

  const sentece_id = `admin_sentence-${
    import.meta.env.VITE_APP_STAGE?.trim() === "prod" ? "prod" : "dev"
  }`;

  useEffect(() => {
    const unsubscribe = listenToSentenceData(sentece_id, (data) => {
      setSentenceData(data?.[sentece_id]);
    });
    return () => unsubscribe();
  }, []);

  const sentenceStarterAI = async () => {
    try {
      updatePopUpAtOnce(true);
      if (currentUser?.role === "student") {
        await dispatch(
          setPopUpHandler({
            open: true,
            type: "savedSentenceStarter",
            data: studentDataFilter[0],
          })
        );
        return;
      }
      await dispatch(
        setPopUpHandler({ open: true, type: "sentenceStarterOption" })
      );
    } catch (error) {
      console.log("error");
    }
  };

  const onSentanceClick = (type) => {
    if(!sentenceData?.id){
      showAlert("No sentence starter is provided.", "error")
      return;
    }
    sentenceData.view = type;
    updatePopUpAtOnce(true);
    invokeSentenceView(sentenceData);
  };

  const handleForDistrictAdmin = () =>{
    if (currentUser.role === 'admin') {
      showAlert('Please login as teacher!', 'error')
      return true;
    }
    return false;
  }
  
  const handleMenuClick = async (e) => {
     if(checkPopUpAtOnce()){
      return;
    }
    setIsDropdownOpen(false);
    revokeSentenceView();
    if (e.key === "AI") {
      if (window.innerWidth <= 700) {
        showAlert("Sentence Starter is designed for larger screens, and is not supported on smaller or mobile screens.", "error")
        updatePopUpAtOnce(false);
        return;
      }
      if (handleForDistrictAdmin()) return; sentenceStarterAI();
      return;
    }
    onSentanceClick(e.key);
    await dispatch(setPopUpHandler({}));
  };

  const showAIOption = useMemo(() => {
    return (
      (hasData?.superLesson && Object.keys(hasData.superLesson).length > 0) ||
      (hasData?.differnationLesson &&
        Object.keys(hasData.differnationLesson).length > 0) ||
      (hasData?.sentenceData && Object.keys(hasData.sentenceData).length > 0)
    );
  }, [hasData]);

  const menuNav = useMemo(() => {
    const items = [
      {
        key: 'spanish',
        label: (
          <span style={{
            display: "block",
            whiteSpace: "nowrap",
            backgroundColor: sentenceView?.pages?.view === "spanish" ? "#020A52" : "transparent",
            color: sentenceView?.pages?.view === "spanish" ? "#fff" : "inherit",
            padding: "5px 12px",
            margin: "-5px -12px",
            borderRadius: "4px"
          }}>
            Spanish Sentence
          </span>
        )
      },
      {
        key: 'english',
        label: (
          <span style={{
            display: "block",
            whiteSpace: "nowrap",
            backgroundColor: sentenceView?.pages.view === "english" ? "#020A52" : "transparent",
            color: sentenceView?.pages?.view === "english" ? "#fff" : "inherit",
            padding: "5px 12px",
            margin: "-5px -12px",
            borderRadius: "4px"
          }}>
            English Sentence
          </span>
        )
      }
      
    ];

    if (currentUser?.role === "student" && studentDataFilter?.length > 0) {
      items.push({
        key: 'AI',
        label: (
          <span style={{
            display: "block",
            whiteSpace: "nowrap",
            backgroundColor: sentenceStarterPopUp?.type === "savedSentenceStarter" ? "#020A52" : "transparent",
            color: sentenceStarterPopUp?.type === "savedSentenceStarter" ? "#fff" : "inherit",
            padding: "5px 12px",
            margin: "-5px -12px",
            borderRadius: "4px"
          }}>
            AI Sentence Stater
          </span>
        )
      });
    } else if (showAIOption && currentUser?.role !== "student") {
      items.push({
        key: 'AI',
        label: (
          <span style={{
            display: "block",
            whiteSpace: "nowrap",
            backgroundColor: sentenceStarterPopUp?.type === "sentenceStarterOption" ? "#020A52" : "transparent",
            color: sentenceStarterPopUp?.type === "sentenceStarterOption" ? "#fff" : "inherit",
            padding: "5px 12px",
            margin: "-5px -12px",
            borderRadius: "4px"
          }}>
            Sentence with AI
          </span>
        )
      });
    }

    return {
      items,
      onClick: handleMenuClick
    };
  }, [currentUser?.role, studentDataFilter?.length, showAIOption, handleMenuClick, sentenceView?.view]);

  return (
    <>
      <Dropdown
        menu={menuNav}
        placement="bottomLeft"
        trigger={["click"]}
        getPopupContainer={(trigger) => trigger.parentNode}
        onOpenChange={handleVisibleChange}
        open={isDropdownOpen}
      >
        <AssignmentPlan>
          <AssignmentPlanInner>
            Sentence starters{" "}
            <DownOutlined
              className={isDropdownOpen ? "rotate-icon" : ""}
              style={{
                transition: "transform 0.3s",
                transform: isDropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </AssignmentPlanInner>
        </AssignmentPlan>
      </Dropdown>
    </>
  );
};

export default SentenceViewTooling;

const AssignmentPlan = styled.button`
    // background-image: linear-gradient(to right,${COLOR.indigo500}, ${COLOR.indigo600});
    background-color: #020A52;
    // border: none;
    border:1px solid #020A52;
    cursor: pointer;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    height: 27.2px;
    color: white;
    z-index: 1;
    padding: 1px;
    font-size: 12.1px;
    font-weight: 400;
    margin-left: 4px;

    @media only screen and (max-width: 1367px){
      margin-top:5px;
    }
}
`;

const AssignmentPlanInner = styled.div`
  display: flex;
  // flex-direction: column;
  flex-direction: row;
  gap: 5px;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background-color: transparent;
  border: 1px solid #fff;
  padding-left: 6px;
  padding-right: 6px;
`;
